import React from 'react';
import cx from 'classnames';
import { Form as FinalForm } from 'react-final-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import type {
  LoginRequest,
  SignupRequest,
} from '@zola-helpers/client/dist/es/redux/auth/authActions';
import { BusinessUnit } from '@zola-helpers/client/dist/es/@types';
import detectBusinessUnit from '@zola-helpers/client/dist/es/util/detectBusinessUnit';

import FormInput from '../../../Form/InputFieldV2/FormInput';
// @ts-expect-error: allow JS import
import ButtonV2 from '../../../Button/ButtonV2';
// @ts-expect-error: allow JS import
import Link from '../../../Link/Link';
import FacebookLoginButton from '../FacebookLoginButton';
import AppleLoginButton from '../AppleLoginButton';

import useAuthErrorHandler from '../../../../hooks/useAuthErrorHandler';
import useGuessTimezone from '../../../../hooks/useGuessTimezone';
import useRecaptchaV3, { ZolaRecaptchaV3Actions } from '../../../../hooks/useRecaptchaV3';
import * as Validations from '../../../Form/util/validations';
import { login, signup } from '../../../../actions/AuthActions';

import { AuthState, AuthFormV1Props } from '../../types';

import styles from './AuthForm.module.less';
import containerStyles from '../../AuthForms.module.less';

// For backwards compatibility
export type AuthFormProps = AuthFormV1Props;

const AuthForm: React.FC<AuthFormV1Props> = ({
  authState,
  setAuthState,
  onAny200Login,
  signupCallback,
  onClickSignup,
  passwordInputType,
  onTogglePasswordVisibility,
  useRefactoredRecaptcha = false,
}) => {
  const [errorMessage, handleError, preventSubmit] = useAuthErrorHandler();
  const [timezone] = useGuessTimezone();
  const businessUnit = detectBusinessUnit();
  const initialValues = authState === AuthState.SIGNUP ? { timezone } : {};
  const isLogin = authState === AuthState.LOGIN;
  const { executeRecaptcha } = !useRefactoredRecaptcha
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useGoogleReCaptcha()
    : { executeRecaptcha: undefined };
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const refactoredGetToken = useRefactoredRecaptcha ? useRecaptchaV3() : undefined;

  let getRecaptchaToken = (actionName: string): Promise<string> => {
    if (!executeRecaptcha) {
      // eslint-disable-next-line no-console
      console.log('Execute recaptcha not yet available');
      return Promise.resolve('');
    }

    return executeRecaptcha(actionName);
  };

  if (useRefactoredRecaptcha) {
    getRecaptchaToken = refactoredGetToken as (action?: string | undefined) => Promise<string>;
  }

  const onLoginSubmit = (values: LoginRequest): void => {
    getRecaptchaToken(ZolaRecaptchaV3Actions.LOGIN)
      .then((token) => {
        return login({ ...values, captchaV3Token: token })
          .then((data) => onAny200Login(data, values.email))
          .catch(handleError);
      })
      .catch(handleError);
  };

  const onSignupSubmit = (values: SignupRequest) =>
    signup(values).then(signupCallback).catch(handleError);

  const renderTitle = () => {
    switch (authState) {
      case AuthState.SIGNUP: {
        return businessUnit === BusinessUnit.MARKETPLACE ? 'Sign Up' : 'Create Account';
      }
      case AuthState.LOGIN:
      default:
        return 'Log In';
    }
  };

  return (
    <FinalForm
      onSubmit={
        isLogin
          ? (values: LoginRequest | SignupRequest) => onLoginSubmit(values)
          : (values: SignupRequest | LoginRequest) => onSignupSubmit(values as SignupRequest)
      }
      initialValues={initialValues}
    >
      {({ handleSubmit, submitting }) => (
        <>
          <h1 className={containerStyles.title}>{renderTitle()}</h1>
          <form
            id={`auth_${authState.toLowerCase()}`}
            data-testid={`auth_${authState.toLowerCase()}`}
            onSubmit={handleSubmit}
          >
            <FormInput
              className={containerStyles.field}
              name="email"
              type="email"
              label="Email"
              placeholder="Email"
              id="zui-auth__email"
              isRequired
              validate={[Validations.REQUIRED, Validations.EMAIL]}
            />
            <FormInput
              className={cx(containerStyles.field, styles.password)}
              name="password"
              id="zui-auth__password"
              label="Password"
              type={passwordInputType}
              placeholder="Password (8 or more characters)"
              isRequired
              password
              validate={[Validations.REQUIRED, Validations.PASSWORD]}
              onTogglePasswordVisibility={onTogglePasswordVisibility}
            />
            <ButtonV2
              disabled={submitting || preventSubmit}
              fullWidth
              type="submit"
              className={cx(containerStyles.submit, `${authState}-submit`)}
            >
              {isLogin ? 'Log In' : 'Sign Up For Free'}
            </ButtonV2>
          </form>
          {isLogin && (
            <>
              <Link
                arrow={false}
                role="button"
                onClick={() => setAuthState(AuthState.FORGOT)}
                size="smaller"
                className={styles.forgot}
              >
                Forgot Password?
              </Link>
              <hr className={styles.other} />
              <FacebookLoginButton
                buttonText="Log In With Facebook"
                callback={onAny200Login}
                errorCallback={handleError}
              />
              <AppleLoginButton callback={onAny200Login} errorCallback={handleError} />
            </>
          )}
          {errorMessage && (
            <div className={containerStyles.error} data-testid="error-message">
              {errorMessage}
            </div>
          )}
          <div className={styles.footer}>
            {isLogin ? (
              <div className={styles.footerText}>
                Don&#39;t have an account?{' '}
                <Link id="zui-auth__signUpBtn" role="button" onClick={onClickSignup}>
                  {businessUnit === BusinessUnit.STORE ? 'Create Account Now' : 'Sign Up Now'}
                </Link>
              </div>
            ) : (
              <div className={styles.footerText}>
                Already have a Zola account?{' '}
                <Link
                  id="zui-auth__signInBtn"
                  role="button"
                  onClick={() => setAuthState(AuthState.LOGIN)}
                >
                  Sign In Here
                </Link>
              </div>
            )}
            <div className={styles.footerText}>
              Are you a Zola vendor?{' '}
              <Link id="zui-auth__vendor-auth" href="/inspire/vendors/login">
                Sign In Here
              </Link>
            </div>
          </div>
        </>
      )}
    </FinalForm>
  );
};

export default AuthForm;
